import { IconButton } from '@lastbrand/web-ui/icon-button'
import { SaveIcon, SaveSolidIcon } from '@lastbrand/web-ui/icons'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { useProductWishList } from '@src/hooks/useProductWishlist'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { css } from '@styled-system/css'

import type { AddToWishlistButtonProps } from './add-to-wishlist-button.types'
import type { SystemStyleObject } from '@lastbrand/web-ui/types'

export function AddToWishlistButton({
  spid,
  selectedColor,
  wishlistTrackConfig,
  wishlistEnabled = true,
  hideWishlistIcon = false,
  productName,
  wishlistAriaLabel,
  wishlistAriaLabelSelected,
  tileVariant = 'default',
  wishlistButtonCss,
  wishlistDisabled,
}: AddToWishlistButtonProps) {
  const { t } = useTranslation('common')
  const { isNativeView } = useDeviceInfo()
  const isLoggedIn = isBrowser() ? AuthUtil.isLoggedIn() : false
  const { isItemInWishList, addItemToWishList, removeItemFromWishList } = useProductWishList()
  const [isWishlisted, setIsWishlisted] = useState(() => isItemInWishList(spid, selectedColor))

  const resolvedAriaLabel = wishlistAriaLabel ?? t('pc_add_wishlist_aria', { productName: productName ?? '' })
  const resolvedAriaLabelSelected =
    wishlistAriaLabelSelected ?? t('pc_remove_wishlist_aria', { productName: productName ?? '' })

  const isMini = tileVariant === 'mini'
  const isCompact = tileVariant === 'compact'
  const isNarrow = isCompact || isMini

  const showWishlist = wishlistEnabled && !(isNativeView && !isLoggedIn) && !hideWishlistIcon

  useEffect(() => {
    setIsWishlisted(isItemInWishList(spid, selectedColor))
  }, [isItemInWishList, spid, selectedColor])

  const onWishlistToggle = useCallback(() => {
    if (isWishlisted) {
      setIsWishlisted(false)
      removeItemFromWishList(spid, selectedColor, wishlistTrackConfig ?? {}).catch((e: unknown) => {
        console.error(e)
      })
    } else {
      setIsWishlisted(true)
      addItemToWishList(spid, selectedColor, wishlistTrackConfig ?? {}).catch((e: unknown) => {
        console.error(e)
      })
    }
  }, [addItemToWishList, isWishlisted, removeItemFromWishList, selectedColor, spid, wishlistTrackConfig])

  const wishlistIconCss = useMemo<SystemStyleObject>(
    () => ({
      color: 'darkgray',
      flexShrink: 0,
      bg: 'transparent',
      _hover: {
        bg: 'white-trans-70',
      },
      _active: {
        bg: 'white-trans-50',
      },
      ...wishlistButtonCss,
    }),
    [wishlistButtonCss],
  )

  if (!showWishlist) return null

  return (
    <div
      className={css({
        position: 'absolute',
        top: { base: 'space-8', lg: isNarrow ? 'space-8' : 'space-16' },
        right: { base: 'space-8', lg: isNarrow ? 'space-8' : 'space-16' },
        zIndex: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      })}
    >
      <IconButton
        type="button"
        variant="ghost"
        aria-label={isWishlisted ? resolvedAriaLabelSelected : resolvedAriaLabel}
        aria-pressed={isWishlisted}
        onClick={onWishlistToggle}
        disabled={wishlistDisabled}
        css={wishlistIconCss}
      >
        {isWishlisted ? <SaveSolidIcon width={20} height={20} /> : <SaveIcon width={20} height={20} />}
      </IconButton>
    </div>
  )
}
